// Diagram coordinates have Y increasing downward: positive area is clockwise.
const mod=(n,d)=>((n%d)+d)%d;
const point=p=>p&&Number.isFinite(p.x)&&Number.isFinite(p.y);
export function exteriorOutlines(job){
  return (job.drawings||[]).filter(d=>d.closed&&d.pts?.length>=3&&d.pts.every(p=>p.length===2&&p.every(Number.isFinite)));
}
function perimeter(points){
  const edges=points.map((a,i)=>{const b=points[(i+1)%points.length];return {a,b,length:Math.hypot(b[0]-a[0],b[1]-a[1])};});
  return {edges,length:edges.reduce((n,e)=>n+e.length,0),clockwise:points.reduce((n,a,i)=>{const b=points[(i+1)%points.length];return n+a[0]*b[1]-b[0]*a[1];},0)>0};
}
function project(p,edges){
  let along=0,best={distance:Infinity};
  for(const {a,b,length} of edges){
    const t=length?Math.max(0,Math.min(1,((p.x-a[0])*(b[0]-a[0])+(p.y-a[1])*(b[1]-a[1]))/(length*length))):0;
    const x=a[0]+t*(b[0]-a[0]),y=a[1]+t*(b[1]-a[1]),distance=Math.hypot(x-p.x,y-p.y);
    if(distance<best.distance)best={distance,along:along+t*length,x,y};
    along+=length;
  }
  return best;
}
export function routeMatches(job,route){return exteriorOutlines(job).some(d=>JSON.stringify(d.pts)===JSON.stringify(route?.points));}
export function routePlan(route,duration){
  if(!route?.enabled)return {error:'Exterior auto-placement is off.'};
  if(!Array.isArray(route.points)||route.points.length<3||!route.points.every(p=>Array.isArray(p)&&p.length===2&&p.every(Number.isFinite)))return {error:'Choose a closed building outline from Diagram first.'};
  const {edges,length,clockwise}=perimeter(route.points);
  if(length<=0||Math.abs(route.points.reduce((n,a,i)=>{const b=route.points[(i+1)%route.points.length];return n+a[0]*b[1]-b[0]*a[1];},0))<0.001)return {error:'Trace a building outline with a usable perimeter.'};
  if(!point(route.start))return {error:'Tap the starting point on the outline.'};
  if(!['Clockwise','Counterclockwise'].includes(route.direction))return {error:'Choose a walking direction.'};
  if(!Number.isFinite(duration)||duration<=0)return {error:'The video needs a known duration for suggested positions.'};
  const start=project(route.start,edges).along,sign=(route.direction==='Clockwise')===clockwise?1:-1;
  const progress=p=>mod(sign*(project(p,edges).along-start),length);
  if(!route.fullCircuit&&!point(route.end))return {error:'Tap where this partial walk ends, or choose one full circuit.'};
  const end=route.fullCircuit?length:progress(route.end);
  if(end<0.001)return {error:'For a partial walk, choose an end point different from the start.'};
  const anchors=[{time:0,distance:0}];
  const checkpoints=[...(route.checkpoints||[])].sort((a,b)=>a.time-b.time);
  for(const c of checkpoints){
    if(!point(c.position)||!Number.isFinite(c.time)||c.time<=0||c.time>=duration||c.time<=anchors[anchors.length-1].time)return {error:'Checkpoints need different times between the start and end of the video.'};
    let distance=progress(c.position);
    const previous=anchors[anchors.length-1].distance;
    if(route.fullCircuit&&distance<0.001&&previous>0)distance=length;
    if(distance+0.001<previous||distance>end+0.001)return {error:'Checkpoints must follow your walking direction. Adjust the point, time or direction.'};
    anchors.push({time:c.time,distance});
  }
  anchors.push({time:duration,distance:end});
  return {edges,length,start,sign,anchors};
}
export function positionAt(route,duration,time){
  const plan=routePlan(route,duration);if(plan.error||!Number.isFinite(time)||time<0||time>duration)return null;
  const {edges,length,start,sign,anchors}=plan;
  const i=Math.max(1,anchors.findIndex(a=>a.time>=time));
  const a=anchors[i-1],b=anchors[i],fraction=(time-a.time)/(b.time-a.time);
  let distance=mod(start+sign*(a.distance+(b.distance-a.distance)*fraction),length);
  for(const e of edges){if(distance<=e.length&&e.length>0)return {x:Math.round(e.a[0]+(e.b[0]-e.a[0])*distance/e.length),y:Math.round(e.a[1]+(e.b[1]-e.a[1])*distance/e.length)};distance-=e.length;}
  return null;
}
export function photoTime(draft){
  const selected=draft.selectedImage||draft.imagePath;
  const option=draft.imageOptions?.find(f=>f.path===selected);
  if(Number.isFinite(option?.time))return option.time;
  if(draft.capturedImage===selected&&Number.isFinite(draft.capturedFrameTime))return draft.capturedFrameTime;
  if(Number.isFinite(draft.photoCueTimes?.[0]))return draft.photoCueTimes[0];
  return Number.isFinite(draft.start)&&Number.isFinite(draft.end)?(draft.start+draft.end)/2:null;
}
export function suggestPosition(job,clip,draft){
  // Never overwrite approved positions, manual corrections, or older manually placed drafts.
  if(draft.approved||draft.positionSource==='manual'||(draft.position&&draft.positionSource!=='exterior-route'))return draft;
  const time=photoTime(draft);
  const position=routeMatches(job,clip.exteriorRoute)?positionAt(clip.exteriorRoute,clip.duration,time):null;
  return {...draft,position,positionSource:position?'exterior-route':null,positionTime:position?time:null};
}
export function mergeRouteResult(job,clip,result){
  const next={...clip,...result,exteriorRoute:clip.exteriorRoute};
  const old=clip.drafts||[];
  next.drafts=(result.drafts||old).map(d=>suggestPosition(job,next,old.find(o=>o.id===d.id)||d));
  for(const d of old)if(d.approved&&!next.drafts.some(n=>n.id===d.id))next.drafts.push(d);
  return next;
}
